import React from 'react';
import styled from '@emotion/styled';
import { fromTheme } from '@kyruus/ui-theme';
import {
  RichTextRenderer,
  RichTextRendererProps,
  STYLE_RESET_CLASSNAME
} from '@kyruus/rich-text/Renderer';
import { PROVIDER_LOGO_MAX_WIDTH_PX_COUNT } from '@kyruus/provider-components';

export const AboutHeader = styled.h2`
  color: ${fromTheme('color_text')};
  font-size: ${fromTheme('font_size_heading_4')};
  font-weight: ${fromTheme('font_weight_bolder')};
  margin: 0;
  grid-area: title;
`;

export const AboutContentContainer = styled.div`
  margin-top: ${fromTheme('spacing_large')};
  line-height: ${fromTheme('spacing_large')};
  font-size: ${fromTheme('font_size')};
  color: ${fromTheme('color_text')};

  // data can be in plain text (not wrapped in <p> tags) even when summary_data configuration is rich_text
  & div:not(:has(p, ul, ol)) {
    white-space: pre-wrap;
  }
`;

export const AboutContentTitle = styled.h3`
  color: ${fromTheme('color_text')};
  font-size: ${fromTheme('font_size')};
  font-weight: ${fromTheme('font_weight_bolder')};
  margin: 0 0 ${fromTheme('spacing_small')} 0;
  grid-area: title;
`;

export const AboutContentLogoContentContainer = styled.div`
  display: grid;
  grid-template-areas:
    'title logos'
    'text logos';
  grid-template-rows: auto;
  grid-template-columns: 1fr ${PROVIDER_LOGO_MAX_WIDTH_PX_COUNT}px;
  grid-column-gap: ${fromTheme('spacing_large')};

  @media screen and (max-width: ${fromTheme('db_screen_small')}) {
    grid-template-areas:
      'title'
      'logos'
      'text';
    grid-template-columns: none;
    grid-row-gap: ${fromTheme('spacing_small')};
  }
`;

export const AboutContentTextContainer = styled.div`
  grid-area: text;
`;

export const AboutContentLogosContainer = styled.div`
  grid-area: logos;
`;

export const AboutContentText = styled((props: RichTextRendererProps) => (
  <RichTextRenderer
    {...props}
    styleReset="revert" /* reset any styling from the consuming context */
  />
))`
  &.${STYLE_RESET_CLASSNAME} {
    a {
      color: ${(props) =>
        props.disabled
          ? fromTheme('color_text_disabled')
          : fromTheme('color_link')};
      font-size: ${fromTheme('font_size')};
      font-family: ${fromTheme('font_face')};
      text-decoration: none;
      &:hover,
      &:focus {
        text-decoration: ${(props) => (props.disabled ? 'none' : 'underline')};
        text-decoration-thickness: 15%;
        cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
      }
    }

    p&,
    p,
    h3,
    h4,
    h5,
    h6 {
      margin: ${fromTheme('spacing_small')} 0;
      line-height: ${fromTheme('spacing_large')};
      font-size: ${fromTheme('font_size')};
      color: ${fromTheme('color_text')};
      white-space: normal;
    }

    ul,
    ol {
      line-height: ${fromTheme('spacing_large')};
      list-style-position: outside;
      padding: 0;
      margin: 0 0 10px 25px;
      font-size: ${fromTheme('font_size')};
      font-weight: ${fromTheme('font_weight')};
      color: ${fromTheme('color_text')};
      white-space: normal;
    }

    hr {
      margin: 20px 0;
      white-space: normal;
    }

    h6 {
      font-size: 0.75em;
    }
  }
`;
// Match the styling of rich text lists in the AboutContentText component
export const ClinicalSpecialInterestsList = styled.ul`
  line-height: ${fromTheme('spacing_large')};
  list-style-position: outside;
  padding: 0;
  margin: 0 0 10px 25px;
  font-size: ${fromTheme('font_size')};
  font-weight: ${fromTheme('font_weight')};
  color: ${fromTheme('color_text')};
  white-space: normal;
`;
